/**
 * ResearchNeedsMoreInfo — the shared "this record is too sparse to research"
 * state.
 *
 * Rendered from three places so every surface reads identically:
 *   • the full-page Research tab, before a run, when the record already fails
 *     the researchable check
 *   • the record slideout's Research section, in the same pre-run case
 *   • `ResearchDetail`, after a run that ended with `needs_more_info`
 *
 * This is deliberately NOT an error state. Nothing failed — the agent had
 * nothing to search on. Genuine failures keep the error treatment in
 * `ResearchDetail`'s `ErrorState`.
 */

import { FileSearch02 } from '@untitledui/icons';

import { EmptyState } from '@/lib/base/empty-state';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';

import type { ResearchRecordType } from './researchOutcome';

const TITLE = 'Needs more info';

/**
 * What's missing and what to do about it, per record type. Owned here rather
 * than taken from the backend's `ahoy_error_message`, which always names a
 * "contact" even when the record is a company.
 */
const DESCRIPTIONS: Record<ResearchRecordType, string> = {
  contact:
    'Add a name, company, or business email address to research this contact.',
  company: 'Add a company name or domain to research this company.',
};

/** Hoisted so the icon element keeps a stable reference across renders. */
const NEEDS_MORE_INFO_ICON = (
  <FeaturedIcon
    icon={FileSearch02}
    size='md'
    theme='light-outline'
    color='gray'
  />
);

interface ResearchNeedsMoreInfoProps {
  recordType: ResearchRecordType;
}

export function ResearchNeedsMoreInfo({
  recordType,
}: ResearchNeedsMoreInfoProps) {
  return (
    <EmptyState
      variant='noResults'
      title={TITLE}
      description={DESCRIPTIONS[recordType]}
      icon={NEEDS_MORE_INFO_ICON}
      size='sm'
    />
  );
}
